import { HttpHeaders, provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, inject, Injectable } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@ngneat/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LoggedInUser, Role } from './models/useridentity.model';
import { authInterceptor } from './core/auth/auth.interceptor';

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(withInterceptors([authInterceptor])),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            // Preload the default language before the app starts to prevent empty/jumping content
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () => firstValueFrom(translocoService.load(defaultLang));
            },
            multi: true,
        },

        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
    ],
};

@Injectable({
  providedIn: 'root'
})

export class AppConfig {

    public getAuthHeader(): { headers: HttpHeaders } | null {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');    
      if (currentUser && currentUser.token) {
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${currentUser.token}`
        });
        return { headers }; // Return an object with the headers property
      } else {
        return null;
      }
    }
  
    public getToken() {
      let currentUser = JSON.parse(String(localStorage.getItem('currentUser')));     
      if (currentUser && currentUser.token) {
        return currentUser.token;
      }
      else {
        return "";
      }
    }
  
    public getCurrentUserId() {
      let currentUser = JSON.parse(String(localStorage.getItem('currentUser')));    
      if (currentUser && currentUser.userId) {
        return currentUser.userId;
      }
      else {
        return null;
      }
    }
  
    public getCurrentUserRole() {
      if (this.isAuthenticated()) {
        var currentToken = this.getToken();
        const helper = new JwtHelperService();
        const decodedToken = helper.decodeToken(currentToken);
        const role = decodedToken["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
        return role;
      }
      return null;
    }
  
    public getCurrentUser(isUpdateInfo: boolean = false): LoggedInUser | null {
      let currentUserObj = JSON.parse(String(localStorage.getItem('currentUser')));
      let userDetails = JSON.parse(String(localStorage.getItem('currentUser')));
      if (currentUserObj) {
        var currUser = new LoggedInUser();
        currUser.userId = currentUserObj.userId;
        currUser.companyId = currentUserObj.companyId;
        currUser.userName = currentUserObj.userName;
        currUser.email = currentUserObj.email;
        if (isUpdateInfo) {
          currUser.token = currentUserObj.token;
          currUser.roleId = currentUserObj.roleId;
          currUser.validity = currentUserObj.validity;
        }
        currUser.fullName = currentUserObj.fullName;
        currUser.role = Role[Role[currentUserObj.roleId]];
        currUser.tokenExpireDate = new Date(currentUserObj.validity);
        currUser.isSpaOrSa = currentUserObj.isSpaOrSa;
        currUser.isCompanyMoreThenOne = currentUserObj.isCompanyMoreThenOne;
        currUser.isSecurityCodeEnabled = currentUserObj.isSecurityCodeEnabled;
        currUser.isSecurityCodeSet = currentUserObj.isSecurityCodeSet;
        currUser.profilePic = currentUserObj.profilePic;
        return currUser;
      }
      else {
        return null;
      }
    }
  
    public setCurrentUser(model: any) {
      localStorage.removeItem('currentUser');
      model.siteName = "RiskAsessment";
      localStorage.setItem('currentUser', JSON.stringify(model));
      localStorage.setItem('userDetails', JSON.stringify(model));
      localStorage.setItem('accessToken',model.token);
    }

    public setCurrentUserAfterProfileUpdate(model: any) {
      localStorage.setItem('currentUser', JSON.stringify(model));
    }
  
    public removeCurrentUser() {
      localStorage.removeItem('currentUser');
      localStorage.removeItem('userDetails');
    }
  
    public isAuthenticated(): boolean {
      const currentUser = JSON.parse(String(localStorage.getItem('currentUser')));
      if (currentUser !== null && currentUser !== undefined) {
        var currentToken = this.getToken();
        const helper = new JwtHelperService();
        const decodedToken = helper.decodeToken(currentToken);
        var currentDate = Math.floor(Date.now() / 1000);
        if (decodedToken["exp"] > currentDate) {
          return true;
        } else {
          this.removeCurrentUser();
          return false;
        }
      }
      else {
        this.removeCurrentUser();
        return false;
      }
    }
  };
  
